import type { ComboboxItem } from "@mantine/core";
import { Group, Modal, ScrollArea, TextInput } from "@mantine/core";
import { randomId } from "@mantine/hooks";
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import CheckboxGroup from "src/components/shared/_FormInputs/CheckboxGroup/CheckboxGroup";
import { Button } from "src/components/shared/Button/Button";
import ImageUploader from "src/components/shared/ImageUploader/ImageUploader";
import { usePostApi, useUploadFileApi } from "src/hooks";
import { WDRLbid } from "src/pages/Settings/YachtSetup/ShipsStamps/ShipsStamp.utils";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setStampsModalProps } from "src/redux/slices/generatorSlice";
import { Flex } from "src/styled";
import { getYachtId } from "src/utils";
import { Notification } from "src/utils/Notification";

type StampLogbookOption = ComboboxItem & {
  group?: string;
};

interface Props {
  logbooks: StampLogbookOption[];
  refetchStamps: () => void;
}
interface IAddStampForm {
  name: string;
  reports: string[];
  logbooks: string[];
}
const AddShipsStamp = ({ logbooks, refetchStamps }: Props) => {
  const dispatch = useDispatch();
  const { stampsModalProps } = useSelector(state => state.generatorSlice);
  const [stamp, setStamp] = useState<string>("");
  const { upload, isUploading } = useUploadFileApi();
  const { post } = usePostApi();

  const methods = useForm({
    values: {
      name: "",
      reports: [],
      logbooks: [],
    },
  });
  const isReport = useCallback(
    (lbid: string) =>
      lbid === "L10" || lbid === WDRLbid || lbid === "crewListReport",
    [],
  );

  useEffect(() => {
    if (stampsModalProps.stampid) {
      void post({
        request: "get-stamp",
        body: {
          params: { stampid: stampsModalProps.stampid, yachtid: getYachtId() },
        },
        onSuccess: res => {
          if (!res.stamp) return;
          methods.setValue("name", res.stamp.name);

          // Load combined logbooks array and split into reports/logbooks
          const combined = res.stamp.logbooks
            ? JSON.parse(res.stamp.logbooks)
            : [];

          const reportsArr = combined.filter((v: string) => isReport(v));
          const logbooksArr = combined.filter((v: string) => !isReport(v));

          methods.setValue("reports", reportsArr);
          methods.setValue("logbooks", logbooksArr);
          setStamp(`data:image/png;base64, ${res.stamp.data}`);
        },
      });
    } else if (stampsModalProps.opened && !stampsModalProps.stampid) {
      // When creating a NEW stamp, set "All Logbooks" as default
      methods.setValue("logbooks", ["*"]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adding deps changes re-render behaviour
  }, [stampsModalProps]);

  function dataURLtoFile(dataurl: string) {
    const arr = dataurl.split(",");
    const matches = arr[0].match(/:(.*?);/);
    const mime = matches ? matches[1] : "image/png";
    const bstr = atob(arr[arr.length - 1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], randomId(), { type: mime });
  }
  const handleSubmit: SubmitHandler<IAddStampForm> = useCallback(
    async formData => {
      const reports = formData.reports || [];
      const lbs = formData.logbooks || [];

      if (stamp === "" || (reports.length === 0 && lbs.length === 0)) {
        Notification("Error", "Please fill all fields", "red");
        return;
      }

      // If "All" is selected for logbooks, only send "*", don't include individual IDs
      let logbooksToSave = lbs;
      if (lbs.includes("*")) {
        logbooksToSave = ["*"];
      }

      // Merge reports and logbooks for backwards compatibility with backend
      const merged = [...reports, ...logbooksToSave];

      const body: any = {
        name: formData.name,
        yachtid: getYachtId(),
        logbooks: JSON.stringify(merged),
      };
      if (stampsModalProps.stampid) {
        body.stampid = stampsModalProps.stampid;
      }
      console.log({ stamp: body });
      await upload(
        "save-stamp",
        [dataURLtoFile(stamp)],
        body,
        () => {
          Notification("Success", "Stamp added successfully", "green");
          refetchStamps();
        },
        true,
      );
      handleClose();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- adding deps changes re-render behaviour
    [stamp],
  );

  const handleClose = () => {
    dispatch(setStampsModalProps({ opened: false }));
    methods.reset();
    setStamp("");
  };

  const logbooksOnly = useMemo(
    () => logbooks.filter(lb => !isReport(lb.value)),
    [logbooks, isReport],
  );
  const reportsOnly = useMemo(
    () => logbooks.filter(lb => isReport(lb.value)),
    [logbooks, isReport],
  );

  return (
    <Fragment>
      <Modal
        title={stampsModalProps.stampid ? "Edit Stamp" : "Add Stamp"}
        size="xl"
        opened={stampsModalProps.opened}
        onClose={handleClose}>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(handleSubmit)}>
            <Flex style={{ width: "100%" }} gap={30}>
              <Flex direction="column" style={{ flexGrow: 2 }}>
                <TextInput
                  {...methods.register("name")}
                  label="Name"
                  required
                  style={{ marginBottom: "20px", maxWidth: 200 }}
                />
                <ImageUploader
                  image={stamp}
                  setImage={setStamp}
                  isStamp={true}
                />
                <ScrollArea mah={300}>
                  <CheckboxGroup
                    name="reports"
                    control={methods.control}
                    data={reportsOnly}
                    disabled={false}
                    grouped
                  />
                </ScrollArea>
              </Flex>
              <Flex style={{ flexGrow: 1 }}>
                <ScrollArea h={500} w={300}>
                  <CheckboxGroup
                    name="logbooks"
                    control={methods.control}
                    data={logbooksOnly}
                    disabled={false}
                    grouped
                  />
                </ScrollArea>
              </Flex>
            </Flex>
            <Group justify="right" mt={15}>
              <Button type="submit" loading={isUploading}>
                Submit
              </Button>
            </Group>
          </form>
        </FormProvider>
      </Modal>
    </Fragment>
  );
};
export default AddShipsStamp;
